import { useId } from 'react'

export interface SegmentedOption<T extends string> {
  value: T
  label: string
  /** Read by a screen reader in place of `label` when the short form is a code. */
  description?: string
}

interface SegmentedControlProps<T extends string> {
  /** The group's accessible name. Rendered visibly unless `hideLabel`. */
  label: string
  options: readonly SegmentedOption<T>[]
  /**
   * `null` means **nothing chosen**, and it is a real state rather than a
   * placeholder. §2.2 requires the recording condition to be explicit: a default
   * selection would make "EC" the answer for a user who never looked, and mixing
   * conditions in one baseline is a correctness bug the UI can prevent.
   */
  value: T | null
  onChange: (value: T) => void
  hideLabel?: boolean
  disabled?: boolean
}

/**
 * A choose-one group.
 *
 * `.segmented` already exists in `components.css` and `ThemeToggle` hand-rolls
 * it; this is the same markup with the label wiring done once. `aria-pressed` on
 * each button rather than `aria-selected`, because these are buttons in a group
 * and not tabs in a tablist — the same call `ThemeToggle` makes, and the CSS
 * already styles both attributes.
 *
 * Deliberately NOT a radio group: a radiogroup's arrow-key roving focus is the
 * right pattern for a form field with a default, and these controls have no
 * default. `role="group"` plus one tab stop per option is what the rest of this
 * app does and what its `:focus-visible` ring is built for.
 */
export function SegmentedControl<T extends string>({
  label,
  options,
  value,
  onChange,
  hideLabel = false,
  disabled = false,
}: SegmentedControlProps<T>) {
  const labelId = useId()
  return (
    <div className="segmented-field">
      <span className={hideLabel ? 'visually-hidden' : 'field__label'} id={labelId}>
        {label}
      </span>
      <div className="segmented" role="group" aria-labelledby={labelId}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className="segmented__option"
            aria-pressed={value === option.value}
            disabled={disabled}
            onClick={() => onChange(option.value)}
          >
            {option.label}
            {option.description !== undefined && (
              <span className="visually-hidden"> — {option.description}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  )
}
